import DetailSection from '@components/Common/DetailSection';
import ToggleField from '@components/Common/ToggleField';
import ErrorMessage from '@components/Common/ErrorMessage';
import FormField from '@components/Common/FormField';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { getPlatformSettingsQuery, setPlatformSettingMutation } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Button, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface Setting { key: string; value: string }

const WHATSAPP = 'whatsapp.mumbai.inviteUrl';
/**
 * The topic groups inside the Mumbai community, in the order the landing pages list them. Each is
 * optional: a group without a link is not shown on the site.
 */
const WHATSAPP_GROUPS: { key: string; name: string; label: string }[] = [
  { key: 'whatsapp.mumbai.group.general', name: 'waGroupGeneral', label: 'Mintly Mumbai Jobs: General' },
  { key: 'whatsapp.mumbai.group.sales', name: 'waGroupSales', label: 'Jewellery Sales Jobs' },
  { key: 'whatsapp.mumbai.group.manufacturing', name: 'waGroupManufacturing', label: 'Jewellery Manufacturing Jobs' },
  { key: 'whatsapp.mumbai.group.cad', name: 'waGroupCad', label: 'CAD and Designer Jobs' },
  { key: 'whatsapp.mumbai.group.luxuryRetail', name: 'waGroupLuxury', label: 'Luxury Retail Jobs' },
  { key: 'whatsapp.mumbai.group.d2c', name: 'waGroupD2c', label: 'D2C and E-commerce Jobs' },
  { key: 'whatsapp.mumbai.group.freshers', name: 'waGroupFreshers', label: 'Freshers Jobs' },
];
/*
 * These were `referral.directAmount` and `referral.downstreamAmount`, which the backend stopped
 * declaring when the two programmes became independent. `getPlatformSettings` therefore never
 * returned them, so both fields rendered blank whatever was stored, and saving one was refused as
 * `Unknown setting`. The amounts were unreachable from this UI, which is how production ended up
 * with the referral programme enabled and no amount set: switched on, paying nothing, and
 * invisible to every job seeker because the card hides itself below a positive amount.
 */
const SEEKER_AMOUNT = 'referral.jobseekerAmount';
const HR_AMOUNT = 'referral.hrAmount';
const ENABLED = 'referral.enabled';
const MIN_COMPLETENESS = 'referral.minCompleteness';
/*
 * Programme v3 (milestones, placement bonus, monthly draw). All of it is inert until PROGRAMME is
 * TIERS_V2: the backend reads an unset programme as the per-person one, so filling these in ahead
 * of launch changes nothing on the site.
 */
const PROGRAMME = 'referral.programme';
const SEEKER_TIERS = 'referral.jobseekerTiers';
const AMBASSADOR_TIERS = 'referral.ambassadorTiers';
const PLACEMENT_BONUS = 'referral.placementBonus';
const AMBASSADOR_PLACEMENT = 'referral.ambassadorPlacementBonus';
const DRAW_PRIZE = 'referral.drawPrize';
/*
 * HR Ambassadors: candidates in these roles count twice toward an ambassador's milestones. Empty
 * uses the owner's list, which the backend holds and the placeholder shows.
 */
const PRIORITY_ROLES = 'referral.priorityRoles';
const PRIORITY_ROLES_DEFAULT =
  'jewellery sales, store manager, retail sales, visual merchandising, e-commerce, D2C marketing, CRM, customer experience, production, merchandising';

const PROGRAMME_OPTIONS = [
  { value: 'PER_PERSON', label: 'Per friend (the 30 Sep launch)' },
  { value: 'TIERS_V2', label: 'Milestones, placement bonus and draw' },
];

/**
 * Values the site reads without a deploy.
 *
 * The WhatsApp invite shows a "Join our Mumbai jobs group" block to Mumbai job seekers, and only
 * once it is set.
 *
 * The referral fields are the whole programme. An amount is what one qualified referral earns,
 * separately for job seekers and HRs, and a referral earns nothing while its amount is unset. The
 * kill switch and the completeness bar are here too: they were previously settable only by calling
 * `setPlatformSetting` by hand, which is why production has them set while the amounts are not.
 *
 * Amounts are rupees and only rupees. The programme pays Indian users only, by hand to the UPI ID
 * each referrer saves, and UPI moves rupees; there was a currency picker here, and choosing USD
 * would have promised payments nobody could make. The backend now ignores `referral.currency`.
 *
 * The backend checks each value; its refusal ("Enter an amount per qualified referral, e.g. 25")
 * is shown as it is, because it already says the right thing.
 */
const PlatformSettingsPanel: React.FC = () => {
  const { showToast } = useToast();
  const [saved, setSaved] = useState<Record<string, string> | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setError(null);
    fetchGraphQLApiData(getPlatformSettingsQuery())
      .then((r) => {
        const map: Record<string, string> = {};
        (r?.data?.getPlatformSettings ?? []).forEach((s: Setting) => { map[s.key] = s.value ?? ''; });
        setSaved(map);
        setDraft(map);
      })
      .catch(() => setError('Settings could not be loaded.'));
  };

  useEffect(load, []);

  const changed = saved ? Object.keys(draft).filter((k) => (draft[k] ?? '') !== (saved[k] ?? '')) : [];

  const save = async () => {
    setSaving(true);
    try {
      for (const key of changed) {
        await fetchGraphQLApiData(setPlatformSettingMutation(key, (draft[key] ?? '').trim()));
      }
      showToast('Settings saved', 'success');
      load();
    } catch (e: any) {
      showToast(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'Settings were not saved.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const set = (key: string) => (_n: string, v: any) => setDraft((d) => ({ ...d, [key]: String(v ?? '') }));

  return (
    <DetailSection
      title="Site settings"
      icon={<TuneOutlinedIcon />}
      sx={{ mb: 0 }}
      headerAction={
        <Button variant="contained" size="small" onClick={save} disabled={saving || changed.length === 0}>
          {saving ? 'Saving' : 'Save'}
        </Button>
      }
    >
      {error ? (
        <ErrorMessage message={error} onRetry={load} />
      ) : !saved ? (
        <SkeletonLoader rows={3} />
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <FormField
              required={false}
              type="url"
              name="whatsappMumbai"
              label="Mumbai WhatsApp Community link"
              placeholder="https://chat.whatsapp.com/..."
              helperText="The community invite. Shown on the Mumbai landing pages as 'Join Mumbai Jobs Community'. Leave empty to hide it."
              value={draft[WHATSAPP] ?? ''}
              onChange={set(WHATSAPP)}
            />
          </Grid>
          {/* The groups inside the community: two per row, each optional. */}
          {WHATSAPP_GROUPS.map((group) => (
            <Grid key={group.key} size={{ xs: 12, md: 6 }}>
              <FormField
                required={false}
                type="url"
                name={group.name}
                label={`Group: ${group.label}`}
                placeholder="https://chat.whatsapp.com/..."
                value={draft[group.key] ?? ''}
                onChange={set(group.key)}
              />
            </Grid>
          ))}
          {/* One row, three equal columns, short labels: longer labels wrapped to different
              heights and pushed the three inputs out of line. The detail is in the helper text. */}
          <Grid size={{ xs: 12, md: 4 }}>
            <FormField
              required={false}
              type="number"
              name="referralSeekerAmount"
              label="Seeker reward (₹)"
              helperText="Rupees per friend a job seeker refers who signs up, uploads a readable resume and completes their profile. India only. Leave empty and nothing is paid and the card is hidden."
              value={draft[SEEKER_AMOUNT] ?? ''}
              onChange={set(SEEKER_AMOUNT)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormField
              required={false}
              type="number"
              name="referralHrAmount"
              label="HR reward (₹)"
              helperText="Rupees per qualified referral from an HR. Independent of the seeker amount. India only, paid to the UPI ID the HR saves."
              value={draft[HR_AMOUNT] ?? ''}
              onChange={set(HR_AMOUNT)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormField
              required={false}
              type="number"
              name="referralMinCompleteness"
              label="Completeness bar"
              helperText="The profile percentage a referred person must reach to count. 60 is the live value."
              value={draft[MIN_COMPLETENESS] ?? ''}
              onChange={set(MIN_COMPLETENESS)}
            />
          </Grid>
          {/* Programme v3. Empty fields use the owner's figures (shown as placeholders). */}
          <Grid size={{ xs: 12 }}>
            <SelectFormField
              required={false}
              name="referralProgramme"
              label="Referral programme"
              options={PROGRAMME_OPTIONS}
              value={draft[PROGRAMME] || 'PER_PERSON'}
              onChange={set(PROGRAMME)}
              helperText="Switch to milestones only on launch day: clear the review queue first, so earlier rewards are netted off correctly."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormField
              required={false}
              name="referralSeekerTiers"
              label="Seeker milestones"
              placeholder="5:100,10:250,25:750,50:1750"
              helperText="Friends:rupees, ascending. Each milestone pays in full when reached."
              value={draft[SEEKER_TIERS] ?? ''}
              onChange={set(SEEKER_TIERS)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormField
              required={false}
              name="referralAmbassadorTiers"
              label="HR Ambassador milestones"
              placeholder="10:200,25:600,50:1500,100:3500,200:8000"
              helperText="Same format. Used once the ambassador programme opens."
              value={draft[AMBASSADOR_TIERS] ?? ''}
              onChange={set(AMBASSADOR_TIERS)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormField
              required={false}
              type="number"
              name="referralPlacementBonus"
              label="Placement bonus (₹)"
              placeholder="500"
              helperText="When a friend a job seeker referred is hired."
              value={draft[PLACEMENT_BONUS] ?? ''}
              onChange={set(PLACEMENT_BONUS)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormField
              required={false}
              name="referralAmbassadorPlacement"
              label="Ambassador placement (₹)"
              placeholder="1000:500"
              helperText="Priority role:other role."
              value={draft[AMBASSADOR_PLACEMENT] ?? ''}
              onChange={set(AMBASSADOR_PLACEMENT)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormField
              required={false}
              type="number"
              name="referralDrawPrize"
              label="Monthly draw prize (₹)"
              placeholder="1000"
              helperText="Run it from Referrals, Monthly draw, once a month has ended."
              value={draft[DRAW_PRIZE] ?? ''}
              onChange={set(DRAW_PRIZE)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormField
              required={false}
              name="referralPriorityRoles"
              label="Ambassador priority roles"
              placeholder={PRIORITY_ROLES_DEFAULT}
              helperText="Comma-separated. Candidates whose preferred role or job category matches one of these, as whole words, count twice for an ambassador. Leave empty to use the list shown."
              value={draft[PRIORITY_ROLES] ?? ''}
              onChange={set(PRIORITY_ROLES)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            {/* Last, and a toggle rather than a text field, because it is the one control here
                that changes whether anything happens at all. Stored as the strings the backend
                validates, "true" and "false", not as a boolean. */}
            <ToggleField
              name="referralEnabled"
              label="Referral programme running"
              checked={(draft[ENABLED] ?? '') === 'true'}
              onChange={(on: boolean) =>
                setDraft((d) => ({ ...d, [ENABLED]: on ? 'true' : 'false' }))
              }
            />
          </Grid>
        </Grid>
      )}
    </DetailSection>
  );
};

export default PlatformSettingsPanel;
